import type { DmrStatus } from '../../services/admin';
import { Field, Section } from './DeviceDetailsParts';

/** "432.7875 MHz" from Hz; null stays empty. */
export const mhz = (hz: number | null | undefined): string | null =>
  hz ? `${(hz / 1_000_000).toFixed(4).replace(/0$/, '')} MHz` : null;

export const CALL_STATE: Record<string, string> = { IDLE: 'Idle', RX: 'Receiving', TX: 'Transmitting' };
export const AVAILABILITY: Record<string, string> = {
  DISABLED: 'Disabled',
  READY: 'Online',
  STARTING: 'Starting',
  BLOCKED_BY_VENDOR_APP: "Blocked - Inrico's DMR app is active",
  NOT_PROVISIONED: 'Not provisioned for this app',
  FAILED: 'Failed',
  STOPPED: 'Stopped',
  UNKNOWN: 'Unknown',
};

// Device Details section for DMR - the IRC380's third radio function next to Primary Group and
// Secondary Group. Read-only: what the radio last reported; nothing here changes the radio.
/** What the Main PTT (and a remote speaker mic) drives; the Secondary PTT drives the other. */
export const mainPttLabel = (target: string | null | undefined): string =>
  target === 'DMR' ? 'DMR (Secondary PTT: Primary Group)' : 'Primary Group (Secondary PTT: DMR)';

/** Who switched DMR on: the server setting or the operator's local enable on the radio. */
export const sourceLabel = (source: string | null | undefined): string | null =>
  source === 'LOCAL' ? 'Locally on the radio (operator)' : source === 'SERVER' ? 'Server setting' : null;

/** The radio reports that its operator enabled DMR locally (in force, independent of dmr_enabled). */
export const isLocal = (status: DmrStatus | null | undefined): boolean => status?.dmr_source === 'LOCAL';

export default function DmrDiagnostics({ status }: { status: DmrStatus | null }) {
  if (!status) {
    return (
      <Section title="DMR">
        <p className="text-sm text-gray-400 col-span-2 md:col-span-3" data-dmr-no-report>
          The radio has not reported its DMR module yet.
        </p>
      </Section>
    );
  }
  const digital = status.channel_type === 'DIGITAL';
  return (
    <Section title="DMR">
      <Field label="DMR module" value={AVAILABILITY[status.availability] ?? status.availability} />
      <Field label="Enabled by" value={sourceLabel(status.dmr_source)} />
      <Field label="Power" value={status.power} />
      <Field label="DMR ID" value={status.radio_id} />
      <Field label="Main PTT" value={mainPttLabel(status.main_ptt_target)} />
      <Field label="State" value={CALL_STATE[status.call_state] ?? status.call_state} />
      <Field label="Module" value={status.module_model} />
      <Field label="Hardware" value={status.hardware_version} />
      <Field label="Firmware" value={status.firmware_version} />
      <Field label="Protocol" value={status.protocol_version} />
      <Field label="Frequency range" value={status.frequency_range} />
      <Field label="Zone" value={status.zone_name ?? status.zone_number} />
      <Field label="Channel" value={status.channel_name ? `${status.channel_name} (${status.channel_number})` : status.channel_number} />
      <Field label="Channel type" value={status.channel_type === 'DIGITAL' ? 'Digital' : status.channel_type === 'ANALOG' ? 'Analog' : status.channel_type} />
      <Field label="RX frequency" value={mhz(status.rx_frequency_hz)} />
      <Field label="TX frequency" value={mhz(status.tx_frequency_hz)} />
      {digital && <Field label="Color Code" value={status.color_code} />}
      {digital && <Field label="Time Slot" value={status.time_slot} />}
      <Field label="Last update" value={new Date(status.reported_at).toLocaleString()} />
      {status.last_error && (
        <Field
          label="Last error"
          value={`${status.last_error}${status.last_error_code !== null ? ` (${status.last_error_code})` : ''}${status.last_error_at ? ` - ${new Date(status.last_error_at).toLocaleString()}` : ''}`}
        />
      )}
    </Section>
  );
}

// Compact DMR box for the radio editor, next to the Primary Group and Secondary Group boxes. A
// radio whose operator enabled DMR locally says so, whatever the server setting is; onRevoke (when
// given) sends an explicit dmr_enabled = false, which revokes the local enable on the radio.
export function DmrBox({ enabled, status, onRevoke, revoking }: {
  enabled: boolean;
  status: DmrStatus | null;
  onRevoke?: () => void;
  revoking?: boolean;
}) {
  const channel = status?.channel_name ? `${status.channel_name}${status.channel_type ? ` (${status.channel_type === 'DIGITAL' ? 'digital' : 'analog'})` : ''}` : null;
  const local = isLocal(status);
  const state = status && status.availability !== 'DISABLED' ? ` - ${AVAILABILITY[status.availability] ?? status.availability}` : '';
  return (
    <div className="block" data-dmr-box>
      <p className="text-sm font-semibold text-gray-300 mb-1">DMR</p>
      <div className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-sm text-white space-y-0.5">
        <p>{local ? `Enabled locally on the radio${state}` : !enabled ? 'Disabled' : `Enabled${state}`}</p>
        {local && (
          <p className="text-amber-300" data-dmr-local>
            The operator enabled DMR on the radio (server setting: {enabled ? 'enabled' : 'disabled'}). A DMR change saved here replaces it.
          </p>
        )}
        {local && onRevoke && (
          <button
            type="button"
            className="mt-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs disabled:opacity-50"
            onClick={onRevoke}
            disabled={revoking}
            data-dmr-revoke
          >
            {revoking ? 'Turning off…' : 'Turn off local DMR (set DMR disabled)'}
          </button>
        )}
        {status && (status.zone_name || channel) && (
          <p className="text-gray-400">{[status.zone_name, channel].filter(Boolean).join(' / ')}</p>
        )}
        {status && <p className="text-gray-400">Main PTT: {mainPttLabel(status.main_ptt_target)}</p>}
      </div>
    </div>
  );
}
